import React from 'react';
import Icon from '../../../components/AppIcon';


const ReportTemplateSelector = ({ selectedTemplate, onTemplateSelect }) => {
  const templates = [
    {
      id: 'ransomware',
      name: 'Ransomware Investigation',
      description: 'Comprehensive template for tracking ransomware payments and fund movements',
      icon: 'Shield',
      sections: ['Executive Summary', 'Attack Timeline', 'Payment Analysis', 'Fund Tracing', 'Recovery Recommendations'],
      estimatedPages: '8-12 pages'
    },
    {
      id: 'exchange-theft',
      name: 'Exchange Theft Analysis',
      description: 'Detailed analysis template for cryptocurrency exchange security breaches',
      icon: 'Building2',
      sections: ['Incident Overview', 'Breach Analysis', 'Stolen Assets Tracking', 'Laundering Patterns', 'Attribution'],
      estimatedPages: '12-18 pages'
    },
    {
      id: 'money-laundering',
      name: 'Money Laundering Investigation',
      description: 'Template for complex money laundering schemes through cryptocurrency',
      icon: 'GitBranch',
      sections: ['Scheme Overview', 'Transaction Patterns', 'Mixing Services', 'Cash-out Methods', 'Legal Analysis'],
      estimatedPages: '15-25 pages'
    },
    {
      id: 'fraud-investigation',
      name: 'Fraud Investigation',
      description: 'General fraud investigation template for various cryptocurrency crimes',
      icon: 'Search',
      sections: ['Case Summary', 'Evidence Collection', 'Transaction Analysis', 'Suspect Identification', 'Conclusions'],
      estimatedPages: '6-10 pages'
    },
    {
      id: 'compliance-audit',
      name: 'Compliance Audit',
      description: 'Template for regulatory compliance and audit documentation',
      icon: 'FileCheck',
      sections: ['Compliance Overview', 'Risk Assessment', 'Transaction Review', 'Recommendations', 'Action Plan'],
      estimatedPages: '10-15 pages'
    },
    {
      id: 'custom',
      name: 'Custom Report',
      description: 'Build a custom report from scratch with flexible sections',
      icon: 'Settings',
      sections: ['Customizable sections based on investigation needs'],
      estimatedPages: 'Variable'
    }
  ];

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Report Templates</h3>
          <p className="text-sm text-text-secondary mt-1">Choose a template to get started quickly</p>
        </div>
        <Icon name="FileText" size={24} color="var(--color-primary)" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates?.map((template) => (
          <div
            key={template?.id}
            className={`
              relative p-4 rounded-lg border-2 cursor-pointer transition-forensic
              ${selectedTemplate === template?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
              }
            `}
            onClick={() => onTemplateSelect(template?.id)}
          >
            <div className="flex items-start space-x-3">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-lg
                ${selectedTemplate === template?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary'
                }
              `}>
                <Icon name={template?.icon} size={20} color="currentColor" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-text-primary text-sm mb-1">
                  {template?.name}
                </h4>
                <p className="text-xs text-text-secondary mb-3 line-clamp-2">
                  {template?.description}
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-text-secondary">
                    <span className="font-medium">Sections:</span>
                    <ul className="mt-1 space-y-1">
                      {template?.sections?.slice(0, 3)?.map((section, index) => (
                        <li key={index} className="flex items-center space-x-1">
                          <Icon name="Check" size={12} color="var(--color-success)" />
                          <span>{section}</span>
                        </li>
                      ))}
                      {template?.sections?.length > 3 && (
                        <li className="text-primary">
                          +{template?.sections?.length - 3} more sections
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{template?.estimatedPages}</span>
                    {selectedTemplate === template?.id && (
                      <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedTemplate && (
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-text-primary">Template Selected</span>
          </div>
          <p className="text-sm text-text-secondary">
            {templates?.find(t => t?.id === selectedTemplate)?.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportTemplateSelector;